import { useEffect, useState } from 'react';
import { fetchTownSignals, type TownSignal } from '../api';

export default function Towns() {
  const [towns, setTowns] = useState<{ name: string; signals: TownSignal[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTown, setSelectedTown] = useState<string | null>(null);
  
  useEffect(() => {
    async function loadData() {
      try {
        const response = await fetchTownSignals();
        if (response.ok && response.data) {
          // Group signals by town
          const signals = response.data as TownSignal[];
          const grouped = signals.reduce((acc, signal) => {
            if (!acc[signal.town]) {
              acc[signal.town] = [];
            }
            acc[signal.town].push(signal);
            return acc;
          }, {} as Record<string, TownSignal[]>);
          
          setTowns(
            Object.entries(grouped).map(([name, signals]) => ({ name, signals }))
          );
        }
      } catch (error) {
        console.error('Failed to load towns:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);
  
  if (loading) {
    return (
      <div className="px-4 pt-6 pb-24">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ubuntu-green"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 pt-6 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Town Signals</h1>
        <p className="text-gray-600 mt-1">Community updates and opportunities</p>
      </div>
      
      {towns.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏘️</div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">No towns yet</h2>
          <p className="text-gray-600">Towns and signals will appear here as they're created</p>
        </div>
      ) : (
        <div className="space-y-4">
          {towns.map((town) => (
            <div key={town.name} className="card">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setSelectedTown(selectedTown === town.name ? null : town.name)}
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">🏘️</div>
                  <div>
                    <h3 className="font-medium text-gray-900">{town.name}</h3>
                    <p className="text-sm text-gray-600">
                      {town.signals.length} signal{town.signals.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="text-gray-400">
                  {selectedTown === town.name ? '▲' : '▼'}
                </div>
              </div>
              
              {selectedTown === town.name && (
                <div className="mt-4 space-y-3 animate-slide-up">
                  {town.signals.map((signal) => (
                    <div key={signal.id} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <div className="text-lg">
                          {signal.urgency === 'high' ? '🔴' : signal.urgency === 'medium' ? '🟡' : '🟢'}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{signal.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{signal.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="badge badge-warning">{signal.urgency}</span>
                            <span className="badge badge-info">{signal.type}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
